import { type CSSProperties, type RefObject, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { CheckCircle } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

import { normalizeContactPhoneToE164, SMS_MARKETING_DISCLOSURE } from '@repo/common';
import {
  DataTestIds,
  PhoneVerificationStatus,
  SmsMarketingAccountSource,
  SmsMarketingConsentState,
} from '@repo/frontend-shared';
import { SDSSkeleton } from '@repo/sds/_internal/SDSSkeleton';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSIcon } from '@repo/sds/SDSIcon';
import { SDSPrimitiveButton } from '@repo/sds/SDSPrimitiveButton';
import { SDSText } from '@repo/sds/SDSText';
import { toast } from '@repo/sds/utils/toast';
import { SmsCodeWaitingNotice } from '@repo/ui/components/sms-marketing/sms-code-fields';
import {
  SmsConsentCheckbox,
  SmsNotAConditionNotice,
  SmsPrivacyNotice,
} from '@repo/ui/components/sms-marketing/sms-marketing-disclosure';
import { smsMarketingResultMessageKey } from '@repo/ui/components/sms-marketing/sms-marketing-signup';
import { SmsPhoneField } from '@repo/ui/components/sms-marketing/sms-phone-field';
import { SmsVerifyCodeDialog } from '@repo/ui/components/sms-marketing/sms-verify-code-dialog';
import {
  formatMaskedUsNumber,
  formatMaskedUsPhone,
  isSameUsNumber,
} from '@repo/ui/components/sms-marketing/us-phone-input';
import { useSmsMarketingAccount } from '@repo/ui/components/sms-marketing/use-sms-marketing-account';
import { useSmsMarketingSignupFlow } from '@repo/ui/components/sms-marketing/use-sms-marketing-signup-flow';

import '@repo/ui/components/sms-marketing/sms-consent-box.scss';

const ids = DataTestIds.SMS_MARKETING;

/**
 * What the profile's Save runs: a check before anything is saved, then the SMS changes once the profile has saved.
 * Opting out does not wait for it: unticking the box opts out at once.
 */
export interface ProfileSmsMarketingActions {
  /** False holds the whole save, with the problem shown on the phone field. */
  validate: () => boolean;
  apply: () => Promise<void>;
}

type FieldProblem = 'invalid-phone' | 'consent-needs-phone' | 'confirm-number';

// Placeholder sizes while the status loads, close to the phone field and the consent box they stand in for.
const PHONE_FIELD_HEIGHT = '50px';
const CONSENT_BOX_HEIGHT = '64px';

const PROBLEM_KEYS = {
  'invalid-phone': 'smsMarketing.phoneInvalid',
  'consent-needs-phone': 'smsMarketing.consentNeedsPhone',
  'confirm-number': 'smsMarketing.settings.confirmNumber',
} as const;

interface ProfileSmsMarketingFieldsProps {
  actionsRef: RefObject<ProfileSmsMarketingActions | null>;
  /** The profile itself is saving; the SMS fields wait, so what Save sends is what they show. */
  isSaving: boolean;
  /** Matches the width of the email beside the phone. */
  phoneColumnStyle: CSSProperties;
}

/**
 * The account phone and SMS consent inside the profile form. Unticking the box opts out straight away, so stopping
 * texts never depends on the rest of the profile. The profile's own Save sends a code for a new number or records
 * consent; nothing is enrolled before the server confirms.
 */
export function ProfileSmsMarketingFields({ actionsRef, isSaving, phoneColumnStyle }: ProfileSmsMarketingFieldsProps) {
  const { t } = useTranslation('common');
  const account = useSmsMarketingAccount(SmsMarketingAccountSource.SETTINGS);
  const codeRef = useRef<HTMLDivElement>(null);
  const flow = useSmsMarketingSignupFlow({
    codeRef,
    pendingVerification: account.pendingVerification,
    onSubmit: account.submit,
    onVerify: account.verify,
    onResend: account.resend,
  });
  const [isEditing, setEditing] = useState(false);
  // Undefined follows the server; a tick or untick here is held until Save.
  const [consentChoice, setConsentChoice] = useState<boolean>();
  const [problem, setProblem] = useState<FieldProblem>();
  const [isDialogDismissed, setDialogDismissed] = useState(false);
  const [isOptingOut, setOptingOut] = useState(false);
  // Each request moves focus to the number once the fields are enabled again.
  const [phoneFocusRequest, setPhoneFocusRequest] = useState(0);
  const handledPhoneFocusRequest = useRef(0);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const { status } = account;
  const { step, details, failure } = flow;
  const verifiedPhone =
    status?.verificationStatus === PhoneVerificationStatus.VERIFIED ? (status.maskedPhone ?? undefined) : undefined;
  const isSubscribed = status?.consent === SmsMarketingConsentState.ACTIVE;
  const isNumberInputShown = !verifiedPhone || isEditing;
  const consented = consentChoice ?? isSubscribed;
  const newPhone = isNumberInputShown ? details.phone.trim() : '';
  const isInternationalPhone = newPhone.startsWith('+') && !newPhone.startsWith('+1');
  const isBusy = isSaving || account.isLoading || isOptingOut || flow.busyAction !== undefined;
  const isDialogOpen = step.kind === 'code' && !isDialogDismissed;

  useEffect(() => {
    if (isBusy || phoneFocusRequest === handledPhoneFocusRequest.current) {
      return;
    }
    handledPhoneFocusRequest.current = phoneFocusRequest;
    phoneInputRef.current?.focus();
  }, [phoneFocusRequest, isBusy]);

  function hold(reason: FieldProblem): false {
    setProblem(reason);
    setPhoneFocusRequest(request => request + 1);
    return false;
  }

  function validate(): boolean {
    if (flow.busyAction !== undefined || isOptingOut) {
      return false;
    }
    setProblem(undefined);
    if (newPhone) {
      return normalizeContactPhoneToE164(newPhone) ? true : hold('invalid-phone');
    }
    if (!consented || (isSubscribed && !isEditing)) {
      return true;
    }
    if (verifiedPhone && !isEditing) {
      // The server only takes consent with the full number, which it never sends back, so it is asked for again.
      setEditing(true);
      return hold('confirm-number');
    }
    return hold('consent-needs-phone');
  }

  function finishEditing(): void {
    setEditing(false);
    setConsentChoice(undefined);
    setProblem(undefined);
    flow.changeNumber();
    flow.updateDetails({ phone: '' });
  }

  async function sendCode(phoneE164: string): Promise<void> {
    const outcome = await flow.submit({
      phoneE164,
      consent: consented,
      disclosureVersion: SMS_MARKETING_DISCLOSURE.version,
    });
    if (outcome === 'code-sent') {
      setDialogDismissed(false);
      return;
    }
    if (outcome === 'failed') {
      setPhoneFocusRequest(request => request + 1);
      return;
    }
    // The number was already the verified one on the account, so only the consent choice was recorded.
    if (consented) {
      toast.success(t('smsMarketing.settings.subscribed'));
    }
    finishEditing();
  }

  async function optOut(): Promise<void> {
    setOptingOut(true);
    const isOptedOut = await account.optOut().then(
      () => true,
      (error: unknown) => {
        console.error('[ProfileSmsMarketingFields] opt-out failed', error);
        return false;
      },
    );
    setOptingOut(false);
    // Either way the box follows the server again: unticked once opted out, still ticked when it failed.
    setConsentChoice(undefined);
    if (isOptedOut) {
      toast.success(t('smsMarketing.settings.optedOut'));
    } else {
      toast.error(t('smsMarketing.settings.optOutFailed'));
    }
  }

  async function apply(): Promise<void> {
    const phoneE164 = newPhone ? normalizeContactPhoneToE164(newPhone) : undefined;
    if (phoneE164 && step.kind === 'code') {
      // The code for this number is still waiting, so saving again reopens it rather than sending another.
      setDialogDismissed(false);
      return;
    }
    if (phoneE164) {
      await sendCode(phoneE164);
      return;
    }
    if (isEditing) {
      finishEditing();
      return;
    }
    if (isSubscribed && !consented) {
      await optOut();
    }
  }

  // The form calls the latest pair, which sees what has been entered by then.
  useImperativeHandle(actionsRef, () => ({ validate, apply }));

  async function verifyCode(): Promise<void> {
    // Read before verifying, as the account reloads with the new number once it is proven.
    const isReplacing = verifiedPhone !== undefined;
    if ((await flow.verify()) !== 'finished') {
      return;
    }
    toast.success(t(isReplacing ? 'smsMarketing.settings.numberChanged' : 'smsMarketing.settings.numberAdded'));
    // Verifying reloads the status itself, but quietly; until a reload succeeds the flow's own result stays on
    // screen, as the status would still show the number from before.
    const isRefreshed = await account.reload().then(
      () => true,
      (error: unknown) => {
        console.warn('[ProfileSmsMarketingFields] status reload after verifying failed', error);
        return false;
      },
    );
    if (isRefreshed) {
      finishEditing();
    }
  }

  // A waiting code was asked for with the earlier number and consent, so any change starts over.
  function startOverIfWaiting(): void {
    if (step.kind === 'code') {
      flow.changeNumber();
    }
  }

  function updatePhone(phone: string): void {
    setProblem(undefined);
    // Leaving the field reformats the same number, which must not drop the code just sent for it.
    if (!isSameUsNumber(phone, details.phone)) {
      startOverIfWaiting();
    }
    flow.updateDetails({ phone });
    if (phone.startsWith('+') && !phone.startsWith('+1')) setConsentChoice(false);
  }

  function updateConsent(checked: boolean): void {
    setProblem(undefined);
    startOverIfWaiting();
    setConsentChoice(checked);
    // Stopping texts must always work, so it is recorded now rather than held for a profile Save that might not pass.
    if (!checked && isSubscribed && !isEditing) {
      void optOut();
    }
  }

  function leavePhoneField(): void {
    if (newPhone && !normalizeContactPhoneToE164(newPhone)) {
      setProblem('invalid-phone');
    }
  }

  function startChange(): void {
    setEditing(true);
    // A new number needs its own consent, so the box starts unticked.
    setConsentChoice(false);
    setPhoneFocusRequest(request => request + 1);
  }

  if (account.hasError && !status) {
    return (
      <div style={phoneColumnStyle} data-testid={ids.PROFILE_PHONE}>
        <SDSFlexbox direction="column" gap="no-design-8" alignItems="flex-start">
          <SDSText type="body-medium" color="alert" text={t('smsMarketing.settings.loadFailed')} />
          <SDSButton
            type="button"
            mode="outline"
            size="small"
            text={t('smsMarketing.settings.retry')}
            onClick={() => void account.reload()}
            dataTestId={ids.SETTINGS_RETRY_BUTTON}
          />
        </SDSFlexbox>
      </div>
    );
  }

  if (account.isLoading && !status) {
    return (
      <>
        <div style={phoneColumnStyle} data-testid={ids.PROFILE_LOADING} aria-busy="true">
          <SDSFlexbox direction="column" gap="text-medium" fullWidth>
            <span className="SDSInputLabel">{t('smsMarketing.phoneField.label')}</span>
            <SDSSkeleton height={PHONE_FIELD_HEIGHT} radius="large" />
            <output className="sr-only">{t('smsMarketing.settings.loading')}</output>
          </SDSFlexbox>
        </div>
        <SDSSkeleton height={CONSENT_BOX_HEIGHT} />
      </>
    );
  }

  const problemKey = problem && PROBLEM_KEYS[problem];

  return (
    <>
      <div style={phoneColumnStyle} data-testid={ids.PROFILE_PHONE}>
        <SDSFlexbox direction="column" gap="no-design-4" fullWidth>
          {step.kind === 'verified' || step.kind === 'already-enrolled' ? (
            <SDSFlexbox direction="column" gap="text-medium" fullWidth>
              <span className="SDSInputLabel">{t('smsMarketing.phoneField.label')}</span>
              <output data-testid={ids.RESULT_MESSAGE}>
                <SDSText type="body-medium" text={t(smsMarketingResultMessageKey(step))} isInline />
              </output>
            </SDSFlexbox>
          ) : isNumberInputShown ? (
            <SmsPhoneField
              allowInternational
              inputRef={phoneInputRef}
              value={details.phone}
              errorMessage={problemKey && t(problemKey)}
              isDisabled={isBusy}
              onChange={updatePhone}
              onBlur={leavePhoneField}
              labelAction={
                isEditing && (
                  <LinkAction
                    text={t('smsMarketing.settings.cancelChange')}
                    isDisabled={isBusy}
                    onClick={finishEditing}
                    dataTestId={ids.PROFILE_CANCEL_CHANGE_BUTTON}
                  />
                )
              }
            />
          ) : (
            <SmsPhoneField
              allowInternational
              value={
                verifiedPhone?.startsWith('+1')
                  ? formatMaskedUsNumber(verifiedPhone)
                  : (verifiedPhone ?? '').replaceAll('*', '•')
              }
              isReadOnly
              onChange={() => undefined}
              rightIcon={
                <SDSIcon icon={<CheckCircle />} color="primary" ariaLabel={t('smsMarketing.settings.verified')} />
              }
              labelAction={
                <LinkAction
                  text={t('smsMarketing.settings.change')}
                  isDisabled={isBusy}
                  onClick={startChange}
                  dataTestId={ids.PROFILE_CHANGE_NUMBER_BUTTON}
                />
              }
            />
          )}
          {step.kind === 'code' && isDialogDismissed && (
            <SmsCodeWaitingNotice
              phone={formatMaskedUsPhone(step.maskedPhone)}
              isDisabled={isBusy}
              onEnterCode={() => setDialogDismissed(false)}
            />
          )}
          {!isDialogOpen && (
            <div role="alert" data-testid={ids.ERROR_MESSAGE}>
              {failure && <SDSText type="body-small" color="alert" text={t(`smsMarketing.errors.${failure}`)} />}
            </div>
          )}
        </SDSFlexbox>
      </div>
      <SDSFlexbox direction="column" gap="no-design-8" fullWidth dataTestId={ids.PROFILE_CONSENT}>
        <SmsConsentCheckbox
          className="SmsConsentBox"
          labelType="body-medium"
          isChecked={consented}
          isDisabled={isBusy || isInternationalPhone}
          onChange={updateConsent}
        />
        {isInternationalPhone && <SDSText type="body-small" color="muted" text={t('smsMarketing.marketingUsOnly')} />}
        <SmsNotAConditionNotice />
        <SmsPrivacyNotice />
      </SDSFlexbox>
      {step.kind === 'code' && !isDialogDismissed && (
        <SmsVerifyCodeDialog
          flow={flow}
          codeRef={codeRef}
          title={t(verifiedPhone ? 'smsMarketing.verifyDialog.titleNewNumber' : 'smsMarketing.verifyDialog.title')}
          dismissLabel={t('smsMarketing.verifyDialog.cancel')}
          maskedPhone={step.maskedPhone}
          resendAvailableAt={step.resendAvailableAt}
          onDismiss={() => setDialogDismissed(true)}
          onVerify={() => void verifyCode()}
          onChangeNumber={() => {
            flow.changeNumber();
            setPhoneFocusRequest(request => request + 1);
          }}
        />
      )}
    </>
  );
}

function LinkAction({
  text,
  isDisabled,
  onClick,
  dataTestId,
}: {
  text: string;
  isDisabled: boolean;
  onClick: () => void;
  dataTestId: string;
}) {
  return (
    <SDSPrimitiveButton isDisabled={isDisabled} onClick={onClick} dataTestId={dataTestId}>
      <SDSText color="primary" type="body-small" text={text} />
    </SDSPrimitiveButton>
  );
}
